// Who on the team can do what in this portal, and the one place to change it.
//
// Two things about roles are easy to get wrong from a screen like this, so both
// are stated on the page rather than left to be discovered:
//
// A role change is not immediate. `cognito:groups` is a claim inside the ID
// token, so it takes effect when that token next refreshes -- up to half an hour.
// Anyone reaching for this page to cut off access right now is reaching for the
// wrong control, and the right one is disabling the account.
//
// A viewer is usually a viewer by default rather than by decision. Cognito creates
// a federated account on first sign-in in no group at all, and the API falls back
// to the read-only role. That is deliberate, but "nobody has decided yet" and
// "decided to be read-only" are different situations, so they look different here.
import { Fragment, useEffect, useState } from 'react';
import { listStaff, setStaffRole, type StaffMember, type StaffRole } from '../api';
import { ROLE_LABELS, useCan, useStaff } from '../staff';

const ROLES: StaffRole[] = ['admin', 'support', 'viewer'];

// What each role can do, in the terms someone assigning it would think in. Kept
// deliberately short: the authority is CAPABILITIES_FOR_ROLE in roles.mjs, and a
// longer paraphrase here would be a second description of the same table, free to
// drift from it.
const ROLE_SUMMARY: Record<StaffRole, string> = {
  admin: 'Everything, including changing these roles and opening partner files.',
  support: 'Reset passwords and keys, list filenames. Cannot change folders or open files.',
  viewer: 'Read the roster and the access map. Changes nothing.',
};

export function AdminStaffPage() {
  const me = useStaff();
  const mayEdit = useCan('staff:write');
  const [staff, setStaff] = useState<StaffMember[]>();
  const [error, setError] = useState<string>();

  const reload = async () => {
    setError(undefined);
    try {
      setStaff(await listStaff());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const admins = staff?.filter((entry) => entry.role === 'admin').length ?? 0;

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>Staff</h1>
          <p className="hint">
            Everyone who can sign in to this portal, and what their role lets them do. Partner
            accounts are on the Users page — these are internal.
          </p>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      {!staff ? (
        <p>Loading…</p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Sign-in</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <StaffRow
                  key={member.username}
                  member={member}
                  isSelf={member.username === me?.username}
                  // The last administrator cannot be demoted. The API refuses it
                  // too -- this only keeps the select from offering a change that
                  // would come back as an error.
                  isLastAdmin={member.role === 'admin' && admins <= 1}
                  mayEdit={mayEdit}
                  onChanged={reload}
                />
              ))}
            </tbody>
          </table>

          <div className="card wide">
            <h2>What the roles mean</h2>
            <dl>
              {/* A Fragment, so the dt/dd pairs are direct children of the dl and
                  land in its two grid columns. A wrapper element would put each
                  pair in one cell. */}
              {ROLES.map((role) => (
                <Fragment key={role}>
                  <dt>{ROLE_LABELS[role] ?? role}</dt>
                  <dd>{ROLE_SUMMARY[role]}</dd>
                </Fragment>
              ))}
            </dl>
            <p className="hint">
              A role change takes effect when the person's session token next refreshes, which can
              be up to 30 minutes. To stop someone using the portal immediately, disable their
              account in the AWS console instead — this page cannot do that, and deliberately so:
              creating, deleting and disabling staff accounts stays where it leaves an audit record
              naming a person.
            </p>
            {!mayEdit && (
              <p className="hint">
                Your role can see this list but not change it. Ask an administrator.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function StaffRow({
  member,
  isSelf,
  isLastAdmin,
  mayEdit,
  onChanged,
}: {
  member: StaffMember;
  isSelf: boolean;
  isLastAdmin: boolean;
  mayEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  // The pending selection, separate from what is saved, so changing the dropdown
  // is not itself the action. A role change is worth a deliberate second click.
  const [choice, setChoice] = useState<StaffRole>(member.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const locked = isSelf || isLastAdmin;
  const dirty = choice !== member.role;

  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await setStaffRole(member.username, choice);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setChoice(member.role);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        {member.displayName ?? member.username}
        <p className="hint">{member.email ?? member.username}</p>
        {!member.enabled && <span className="badge bad">disabled</span>}
      </td>
      <td>
        {mayEdit && !locked ? (
          <>
            <select
              aria-label={`Role for ${member.displayName ?? member.username}`}
              value={choice}
              onChange={(event) => setChoice(event.target.value as StaffRole)}
              disabled={busy}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role] ?? role}
                </option>
              ))}
            </select>
            {dirty && (
              <p>
                <button type="button" onClick={() => void save()} disabled={busy}>
                  {busy ? 'Saving…' : `Make ${ROLE_LABELS[choice] ?? choice}`}
                </button>{' '}
                <button type="button" className="link" onClick={() => setChoice(member.role)}>
                  Cancel
                </button>
              </p>
            )}
          </>
        ) : (
          <span className="badge">{ROLE_LABELS[member.role] ?? member.role}</span>
        )}
        {/* Why a row is not editable, next to the row rather than in a legend.
            Both reasons are guards in the API, so this is the explanation for a
            control that is genuinely absent, not a styling choice. */}
        {isSelf && <p className="hint">You — another administrator has to change this</p>}
        {isLastAdmin && !isSelf && (
          <p className="hint">The only administrator — promote someone else first</p>
        )}
        {!member.explicit && <p className="hint">by default — no role assigned yet</p>}
        {error && <p className="error">{error}</p>}
      </td>
      <td>
        {member.federated ? (
          'Entra SSO'
        ) : (
          <>
            Password
            {/* Worth flagging on the row: this is the account that still works when
                Entra or the SAML endpoint is down, so demoting it is a decision
                about disaster recovery and not only about this person. */}
            <p className="hint">the only kind of account that works if Entra is down</p>
          </>
        )}
      </td>
    </tr>
  );
}
